import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Star, ChevronDown, HelpCircle, ArrowRight, ClipboardCheck, Shield, GraduationCap, Users } from 'lucide-react';
import MembresiasLayout from './MembresiasLayout';
import QualificationFormModal from '@/components/QualificationForm';
import { PLAN_ORDER, publicCatalog } from '@/config/membershipCatalog';
import { useSiteLang } from '@/components/site/SiteLang';

const NAV = '#0a1628';
const GOLD = '#c9a84c';
const CREAM = '#faf9f6';
const SERIF = "'Playfair Display', serif";

const CAT = { es: publicCatalog('es'), en: publicCatalog('en') };

// Fila → en qué planes está incluida (A = Access, P = PRO, X = Premium)
const COL_KEY = { starter: 'A', basica: 'P', premium: 'X' };

const T = {
  es: {
    heroTitle: 'Entra en',
    heroCircle: 'El Círculo',
    heroText: 'El mercado inmobiliario que nunca estuvo al alcance de todos, ahora sí: oportunidades reales fuera del mercado, formación para entenderlas y una comunidad con la que decidir con criterio. Elige el nivel de acceso que encaja con tu momento.',
    pillars: 'Acceso · Formación · Comunidad · Eventos',
    includesCert: 'Incluye certificación',
    membership: 'Membresía',
    seeIncludes: 'Ver qué incluye',
    trust: ['Comunidad privada de miembros', 'Período de satisfacción de 15 días', 'Formación en español'],
    compareKicker: 'Comparativa',
    compareTitle: 'Qué incluye cada membresía',
    includesCol: 'Incluye',
    included: 'Incluido',
    notIncluded: 'No incluido',
    faqTitle: 'Preguntas frecuentes',
    fitTitle: '¿Qué nivel encaja contigo?',
    rows: [
      { f: 'Pertenencia al club y a la comunidad privada', in: 'APX' },
      { f: 'Área privada de oportunidades inmobiliarias off-market', in: 'APX' },
      { f: 'FHC Academy · contenidos introductorios', in: 'APX' },
      { f: 'Sesiones online del club', in: 'APX' },
      { f: 'Recursos y newsletter del club', in: 'APX' },
      { f: 'Networking entre miembros y profesionales', in: 'APX' },
      { f: 'FHC Academy · formación avanzada', in: 'PX' },
      { f: 'Sesiones especializadas en directo', in: 'PX' },
      { f: 'Recursos ampliados', in: 'PX' },
      { f: 'Bootcamp Broker Digital · 4 días presenciales', in: 'X' },
      { f: 'Certificación privada FHC como Broker Digital', in: 'X' },
      { f: 'Acompañamiento grupal semanal en directo', in: 'X' },
    ],
    faqs: [
      { q: '¿Qué membresías hay y cuánto cuestan?', a: `Hay tres membresías anuales: Access, PRO y Premium, y se renuevan cada año desde tu fecha de alta. ${CAT.es.priceAnswer}` },
      { q: '¿Qué diferencia hay entre PRO y Premium?', a: `PRO te da la formación avanzada, las sesiones especializadas en directo y los recursos ampliados. Premium añade el Bootcamp Broker Digital presencial, la certificación privada FHC y el acompañamiento grupal semanal. ${CAT.es.bootcampNote}` },
      { q: '¿Qué estoy pagando exactamente?', a: CAT.es.feeScopeNote },
      { q: '¿Qué tipo de oportunidades hay dentro del club?', a: 'Oportunidades inmobiliarias reales que el equipo y sus colaboradores trabajan fuera de los portales, presentadas en el área privada con su documentación y su análisis. Cada miembro decide libremente si participa y en cuál, después de formarse: participar es siempre una decisión aparte y voluntaria.' },
      { q: '¿La certificación es oficial?', a: CAT.es.certNote },
      { q: '¿Puedo subir de nivel más adelante?', a: 'Sí. Escríbenos y te indicamos las condiciones vigentes para pasar a un nivel superior.' },
      { q: '¿Hay período de satisfacción?', a: CAT.es.satisfactionNote },
    ],
  },
  en: {
    heroTitle: 'Step inside',
    heroCircle: 'the Circle',
    heroText: 'The side of the property market that was never open to everyone, now within reach: real off-market opportunities, the training to understand them and a community to help you decide with sound judgement. Choose the level of access that suits where you are now.',
    pillars: 'Access · Training · Community · Events',
    includesCert: 'Includes certification',
    membership: 'Membership',
    seeIncludes: 'See what’s included in',
    trust: ['Private members’ community', '15-day satisfaction period', 'Training delivered in Spanish'],
    compareKicker: 'Compare',
    compareTitle: 'What each membership includes',
    includesCol: 'Includes',
    included: 'Included',
    notIncluded: 'Not included',
    faqTitle: 'Frequently asked questions',
    fitTitle: 'Which tier suits you?',
    rows: [
      { f: 'Membership of the club and its private community', in: 'APX' },
      { f: 'Members’ area with off-market property opportunities', in: 'APX' },
      { f: 'FHC Academy · introductory content', in: 'APX' },
      { f: 'The club’s online sessions', in: 'APX' },
      { f: 'Club resources and newsletter', in: 'APX' },
      { f: 'Networking with members and professionals', in: 'APX' },
      { f: 'FHC Academy · advanced training', in: 'PX' },
      { f: 'Live specialist sessions', in: 'PX' },
      { f: 'Extended resources', in: 'PX' },
      { f: 'Digital Broker Bootcamp · 4 days in person', in: 'X' },
      { f: 'Private FHC certification as a Digital Broker', in: 'X' },
      { f: 'Weekly live group mentoring', in: 'X' },
    ],
    faqs: [
      { q: 'What memberships are there, and how much do they cost?', a: `There are three annual memberships, Access, PRO and Premium, each renewing every year from the date you join. ${CAT.en.priceAnswer}` },
      { q: 'What’s the difference between PRO and Premium?', a: `PRO gives you the advanced training, the live specialist sessions and the extended resources. Premium adds the in-person Digital Broker Bootcamp, the private FHC certification and weekly group mentoring. ${CAT.en.bootcampNote}` },
      { q: 'What exactly am I paying for?', a: CAT.en.feeScopeNote },
      { q: 'What kind of opportunities are there inside the club?', a: 'Real property opportunities that the team and its partners source away from the property portals, presented in the members’ area with full documentation and analysis. Each member decides freely whether to take part, and in which ones, after completing the training: taking part is always a separate, voluntary decision.' },
      { q: 'Is the certification official?', a: CAT.en.certNote },
      { q: 'Can I move up a tier later on?', a: 'Yes. Get in touch and we’ll explain the current terms for moving up to a higher tier.' },
      { q: 'Is there a satisfaction period?', a: CAT.en.satisfactionNote },
    ],
  },
};

export default function MembresiasSelector() {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const cat = publicCatalog(lang);
  const [openFaq, setOpenFaq] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [source, setSource] = useState('membresias');
  const openTest = (src) => { setSource(src); setModalOpen(true); };

  return (
    <MembresiasLayout>
      {/* ═══ HERO ═══ */}
      <section className="relative" style={{ minHeight: 440, background: 'linear-gradient(180deg, #0a1628 0%, #060d1b 100%)' }} data-testid="selector-hero">
        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 pt-16 pb-14 md:pt-24 md:pb-[72px]">
          <p className="inline-block px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] mb-6 border" style={{ color: GOLD, borderColor: `${GOLD}40`, background: `${GOLD}10` }}>
            Freedom Homes Club
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl leading-[1.08] mb-5 text-white" style={{ fontFamily: SERIF }}>
            {t.heroTitle} <span style={{ color: GOLD }}>{t.heroCircle}</span>
          </h1>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)', maxWidth: 600, margin: '0 auto' }}>
            {t.heroText}
          </p>
          <p className="mt-6 text-sm font-semibold italic" style={{ color: GOLD }}>{t.pillars}</p>
          <button type="button" onClick={() => openTest('membresias-hero')} data-testid="selector-hero-cta"
            className="mt-8 inline-flex max-w-full items-center justify-center gap-2 h-14 px-6 sm:px-8 font-bold text-sm tracking-wider transition-all hover:brightness-110"
            style={{ background: GOLD, color: NAV, borderRadius: 4 }}>
            {cat.testCta} <ArrowRight className="w-4 h-4" />
          </button>
          <p className="mt-3 text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{cat.nextStepNote}</p>
        </div>
      </section>

      {/* ═══ TARJETAS ═══ */}
      <section style={{ background: CREAM, padding: '72px 16px 48px' }} data-testid="selector-cards">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {PLAN_ORDER.map((id) => {
            const p = cat.plans[id];
            const featured = id === 'premium';
            const detailLink = id === 'starter' ? null : `/membresias/${id === 'basica' ? 'pro' : id}`;
            return (
              <div key={id} className="relative flex flex-col overflow-hidden"
                   style={featured ? { border: `2px solid ${GOLD}`, background: NAV } : { border: '1px solid #e0ddd6', background: '#fff' }}
                   data-testid={`selector-card-${id}`}>
                {featured && (
                  <span className="absolute top-0 left-0 right-0 text-center py-2 text-xs font-bold uppercase tracking-wider" style={{ background: GOLD, color: NAV }}>
                    <Star className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> {t.includesCert}
                  </span>
                )}
                <div className={`p-7 flex flex-col flex-1 ${featured ? 'pt-14' : ''}`}>
                  <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: GOLD }}>{t.membership}</p>
                  <p className="text-4xl font-bold" style={{ color: featured ? '#fff' : NAV, fontFamily: SERIF }}>{p.name}</p>
                  <p className="text-sm mt-4 mb-6 leading-relaxed" style={{ color: featured ? 'rgba(255,255,255,0.55)' : '#777' }}>{p.tagline}</p>
                  <div style={{ height: 1, background: featured ? 'rgba(255,255,255,0.1)' : '#eee', margin: '0 0 20px' }} />
                  <div className="space-y-3 mb-8 flex-1">
                    {p.features.map((b, i) => (
                      <p key={b} className="flex items-start gap-2.5 text-sm"
                         style={{ color: featured ? (i === 0 ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.75)') : '#555' }}>
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} /> {b}
                      </p>
                    ))}
                  </div>
                  <button type="button" onClick={() => openTest(`membresias-${p.name.toLowerCase()}`)} data-testid={`selector-cta-${id}`}
                    className="w-full min-h-[48px] inline-flex items-center justify-center gap-2 px-5 py-3 font-bold text-sm tracking-wider transition-all hover:brightness-[0.92]"
                    style={featured
                      ? { background: GOLD, color: NAV, borderRadius: 4 }
                      : { color: NAV, borderRadius: 4, border: `2px solid ${NAV}` }}>
                    {cat.testCta} <ArrowRight className="w-4 h-4 flex-shrink-0" />
                  </button>
                  {detailLink && (
                    <Link to={detailLink} data-testid={`selector-detail-${id}`}
                      className="mt-2 min-h-[44px] inline-flex items-center justify-center gap-1.5 text-sm font-semibold hover:underline"
                      style={{ color: featured ? 'rgba(255,255,255,0.75)' : NAV }}>
                      {t.seeIncludes} {p.name} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mt-10">
          {[
            { icon: Users, text: t.trust[0] },
            { icon: Shield, text: t.trust[1] },
            { icon: GraduationCap, text: t.trust[2] },
          ].map((s) => (
            <span key={s.text} className="flex items-center gap-2 text-xs" style={{ color: '#888' }}>
              <s.icon className="w-4 h-4" style={{ color: GOLD }} /> {s.text}
            </span>
          ))}
        </div>
      </section>

      {/* ═══ COMPARATIVA ═══ */}
      <section id="comparativa" style={{ background: NAV, padding: '72px 16px' }} data-testid="selector-comparativa">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: GOLD }}>{t.compareKicker}</p>
          <h2 className="text-2xl md:text-3xl text-center mb-12 text-white" style={{ fontFamily: SERIF }}>{t.compareTitle}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${GOLD}40` }}>
                  <th className="text-left py-4 px-4 font-bold text-white">{t.includesCol}</th>
                  {PLAN_ORDER.map((id) => (
                    <th key={id} className="text-center py-4 px-3 font-bold" style={{ color: id === 'premium' ? GOLD : 'rgba(255,255,255,0.7)', minWidth: 96 }}>
                      {cat.plans[id].name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.rows.map((r, i) => (
                  <tr key={r.f} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                    <td className="py-3.5 px-4 text-white/65">{r.f}</td>
                    {PLAN_ORDER.map((id) => (
                      <td key={id} className="text-center py-3.5 px-3">
                        {r.in.includes(COL_KEY[id])
                          ? <CheckCircle2 className="w-5 h-5 mx-auto" style={{ color: GOLD }} aria-label={t.included} />
                          : <span className="text-white/20" aria-label={t.notIncluded}>—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-8 text-xs text-center text-white/40 leading-relaxed">{cat.bootcampNote}<br />{cat.certNote}</p>
          <div className="mt-10 text-center">
            <button type="button" onClick={() => openTest('membresias-comparativa')} data-testid="selector-compare-cta"
              className="inline-flex max-w-full items-center justify-center gap-2 h-14 px-6 sm:px-8 font-bold text-sm tracking-wider transition-all hover:brightness-110"
              style={{ background: GOLD, color: NAV, borderRadius: 4 }}>
              {cat.testCta} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section id="faq" style={{ background: CREAM, padding: '72px 16px' }} data-testid="selector-faq">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: GOLD }}>FAQ</p>
          <h2 className="text-2xl md:text-3xl text-center mb-12" style={{ color: NAV, fontFamily: SERIF }}>{t.faqTitle}</h2>
          {t.faqs.map((f, i) => (
            <div key={f.q} style={{ borderBottom: '1px solid #ddd' }}>
              <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-3 py-5 text-left group" data-testid={`selector-faq-${i}`} aria-expanded={openFaq === i}>
                <span className="flex items-center gap-3 pr-4 font-semibold group-hover:opacity-80 transition-opacity" style={{ color: NAV }}>
                  <HelpCircle className="w-5 h-5 flex-shrink-0" style={{ color: GOLD }} /> {f.q}
                </span>
                <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform" style={{ color: '#bbb', transform: openFaq === i ? 'rotate(180deg)' : 'none' }} />
              </button>
              {openFaq === i && <p className="pl-8 pb-5 text-sm leading-relaxed" style={{ color: '#777' }}>{f.a}</p>}
            </div>
          ))}
          <div className="mt-10 p-6 text-center border" style={{ borderColor: '#ddd', background: '#fff', borderRadius: 4 }}>
            <ClipboardCheck className="w-6 h-6 mx-auto mb-2" style={{ color: GOLD }} />
            <p className="text-sm font-bold mb-1" style={{ color: NAV }}>{t.fitTitle}</p>
            <p className="text-xs mb-4" style={{ color: '#888' }}>{cat.nextStepNote}</p>
            <button type="button" onClick={() => openTest('membresias-faq')} data-testid="selector-faq-cta"
              className="inline-flex max-w-full items-center justify-center gap-2 h-12 px-6 font-bold text-sm tracking-wider transition-all hover:brightness-[0.92]"
              style={{ background: GOLD, color: NAV, borderRadius: 4 }}>
              {cat.testCta} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <QualificationFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)} source={source} lang={lang} />
    </MembresiasLayout>
  );
}
